class MCartDrawer extends HTMLElement {
  constructor() {
    super();
    this.cartDrawerInner = this.querySelector(".m-cart-drawer__inner");
    this.cartDrawerCloseIcon = this.querySelector(".m-cart-drawer__close");
    this.cartOverlay = this.querySelector(".m-cart__overlay");
    this.rootUrl = window.Shopify.routes.root;

    this.setHeaderCartIconAccessibility();
    this.cartDrawerCloseIcon.addEventListener("click", this.close.bind(this));
    this.addEventListener("click", (event) => {
      if (event.target.closest(".m-cart-drawer__inner") !== this.cartDrawerInner) {
        this.close();
      }
    });
    document.addEventListener("keydown", (event) => {
      if (!this.isOpen) return;
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        this.close();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(this.cartDrawerInner.querySelectorAll("a[href], button, input:not([type=hidden]), select, textarea, [tabindex]")).filter(element =>
        !element.disabled && element.tabIndex >= 0 && element.getClientRects().length && getComputedStyle(element).visibility !== "hidden" && !element.closest("[inert]"));
      const first = focusable[0] || this.cartDrawerInner;
      const last = focusable[focusable.length - 1] || first;
      if (!this.cartDrawerInner.contains(document.activeElement) ||
        (event.shiftKey && document.activeElement === first) ||
        (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({ preventScroll: true });
      }
    });
    this.refineSuccessMessage();
  }

  refineSuccessMessage() {
    const normalizeText = (text) => (text || "").replace(/\s+/g, " ").trim().toLowerCase();
    const candidates = Array.from(this.querySelectorAll("div, p, span")).filter((element) => {
      if (!element || element.closest(".sd-cart-added-alert")) return false;
      return normalizeText(element.textContent) === "product added to cart successfully";
    });

    candidates.forEach((element) => {
      const target =
        element.closest(".m-alert") ||
        element.closest('[class*="alert"]') ||
        element.closest('[role="alert"]') ||
        element.parentElement ||
        element;
      const targetText = normalizeText(target && target.textContent);

      if (!target || target.classList.contains("sd-cart-added-alert")) return;
      if (!targetText.includes("product added to cart successfully") || targetText.length > 120) return;

      target.className = "sd-cart-added-alert";
      target.innerHTML = `
        <span class="sd-cart-added-icon">✓</span>
        <span class="sd-cart-added-copy">
          <span class="sd-cart-added-title">Added to cart</span>
          <span class="sd-cart-added-sub">Ready for checkout.</span>
        </span>
      `;
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLinks = document.querySelectorAll(".m-cart-icon-bubble");
    cartLinks.forEach((cartLink) => {
      cartLink.setAttribute("role", "button");
      cartLink.setAttribute("aria-haspopup", "dialog");
      cartLink.setAttribute("aria-controls", this.id);
      cartLink.setAttribute("aria-expanded", "false");
      cartLink.addEventListener("keydown", event => {
        if (event.key === " " && MinimogSettings.enable_cart_drawer) {
          event.preventDefault();
          this.open(cartLink);
        }
      });
      cartLink.addEventListener("click", (event) => {
        if (MinimogSettings.enable_cart_drawer) {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    else if (!this.contains(document.activeElement)) this.setActiveElement(document.activeElement);
    clearTimeout(this.closeTimer);
    cancelAnimationFrame(this.openFrame);
    this.isOpen = true;
    this.inert = false;
    this.setAttribute("aria-hidden", "false");
    document.querySelectorAll(".m-cart-icon-bubble").forEach(link => link.setAttribute("aria-expanded", "true"));
    this.classList.add("m-cart-drawer--active");
    this.openFrame = requestAnimationFrame(() => {
      this.style.setProperty("--m-bg-opacity", "0.5");
      this.cartDrawerInner.style.setProperty("--translate-x", "0");
      this.cartDrawerCloseIcon.focus({ preventScroll: true });
    });
    window.MinimogEvents.emit(MinimogTheme.pubSubEvents.openCartDrawer);
    document.documentElement.classList.add("prevent-scroll");
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    cancelAnimationFrame(this.openFrame);
    clearTimeout(this.closeTimer);
    this.inert = true;
    this.setAttribute("aria-hidden", "true");
    document.querySelectorAll(".m-cart-icon-bubble").forEach(link => link.setAttribute("aria-expanded", "false"));
    this.style.setProperty("--m-bg-opacity", "0");
    this.cartDrawerInner.style.setProperty("--translate-x", "100%");
    this.closeTimer = setTimeout(() => {
      this.classList.remove("m-cart-drawer--active");
      if (!document.querySelector("m-search-popup[open], #m-menu-drawer.open")) {
        document.documentElement.classList.remove("prevent-scroll");
      }
      if (this.activeElement?.isConnected && (this.contains(document.activeElement) || document.activeElement === document.body)) {
        this.activeElement.focus({ preventScroll: true });
      }
    }, 300);
  }

  renderContents(parsedState) {
    this.classList.contains("m-cart--empty") && this.classList.remove("m-cart--empty");
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });
    this.refineSuccessMessage();
    setTimeout(() => {
      this.open();
    });
  }

  updateCartCount(itemCount) {
    const cartCounts = document.querySelectorAll(".m-cart-count-bubble");
    cartCounts.forEach((cartCount) => {
      if (itemCount > 0) {
        cartCount.textContent = itemCount;
        cartCount.classList.remove("m:hidden");
      } else {
        cartCount.classList.add("m:hidden");
      }
    });
  }

  getCart() {
    return fetchJSON(this.rootUrl + "cart.json");
  }

  onCartDrawerUpdate(updateFooter = true) {
    fetch(`${MinimogSettings.routes.cart}?section_id=cart-drawer`)
      .then((response) => response.text())
      .then((responseText) => {
        this.getSectionsToRender().forEach((section) => {
          if (section.block === "cart-items") {
            const sectionElement = section.selector
              ? document.querySelector(section.selector)
              : document.getElementById(section.id);
            sectionElement.innerHTML = this.getSectionInnerHTML(responseText, section.selector);
          } else {
            if (updateFooter) {
              const sectionElement = section.selector
                ? document.querySelector(section.selector)
                : document.getElementById(section.id);
              sectionElement.innerHTML = this.getSectionInnerHTML(responseText, section.selector);
            }
          }
        });
        this.refineSuccessMessage();
      })
      .catch((e) => {
        console.error(e);
      });
    this.getCart().then((cart) => {
      this.classList.toggle("m-cart--empty", cart.item_count === 0);
      this.updateCartCount(cart.item_count);
    });
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-items]",
        block: "cart-items",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-cart-subtotal]",
        block: "cart-footer",
      },
      {
        id: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
        block: "cart-footer",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser().parseFromString(html, "text/html").querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("m-cart-drawer", MCartDrawer);

class MCartDrawerItems extends MCartTemplate {
  getSectionsToRender() {
    return [
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-items]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-cart-discounts]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-cart-subtotal]",
      },
      {
        id: "MinimogCartDrawer",
        section: "cart-drawer",
        selector: "[data-minimog-gift-wrapping]",
      },
    ];
  }
}

customElements.define("m-cart-drawer-items", MCartDrawerItems);
